'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

export default function GlobalAudioPlayer() {
  const pathname = usePathname();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const STORAGE_KEY = 'bgm_enabled';
  const playlist = useMemo(
    () => [
      '/Hayd Closure Official Lyric Video _004835636.mp3',
      '/Imagine.mp3',
    ],
    []
  );

  const [trackIndex, setTrackIndex] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const [topOffsetPx, setTopOffsetPx] = useState(12);

  const tryPlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      await audio.play();
    } catch {
      // Autoplay may be blocked by browser policies.
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === '1') setEnabled(true);
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const update = () => {
      const header = document.querySelector('header') as HTMLElement | null;
      const h = header?.getBoundingClientRect().height ?? 0;
      const extra = 12;
      setTopOffsetPx(Math.max(extra, Math.round(h + extra)));
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.src = playlist[trackIndex];
    audio.load();

    if (!enabled) {
      audio.pause();
      return;
    }

    void tryPlay();

    const resume = () => {
      void tryPlay();
    };

    window.addEventListener('pointerdown', resume, { once: true });
    window.addEventListener('keydown', resume, { once: true });
    window.addEventListener('touchstart', resume, { once: true, passive: true });
    window.addEventListener('scroll', resume, { once: true, passive: true });

    return () => {
      window.removeEventListener('pointerdown', resume);
      window.removeEventListener('keydown', resume);
      window.removeEventListener('touchstart', resume);
      window.removeEventListener('scroll', resume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackIndex, playlist, enabled]);

  // AI 아바타 상담 영상이 말하는 동안 배경음악 일시정지, 끝나면 다시 재생
  useEffect(() => {
    const onSpeaking = (e: Event) => {
      const audio = audioRef.current;
      if (!audio || !enabled) return;
      if ((e as CustomEvent<boolean>).detail) audio.pause();
      else void tryPlay();
    };
    window.addEventListener('avatar-chat:speaking', onSpeaking);
    return () => window.removeEventListener('avatar-chat:speaking', onSpeaking);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const handleEnded = () => {
    setTrackIndex((prev) => (prev + 1) % playlist.length);
  };

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      // ignore
    }

    const audio = audioRef.current;
    if (!audio) return;

    if (!next) {
      audio.pause();
      return;
    }

    void tryPlay();
  };

  const scrollToPricing = () => {
    const target = document.querySelector('#pricing') as HTMLElement | null;
    if (!target) {
      window.location.href = '/#pricing';
      return;
    }
    const header = document.querySelector('header') as HTMLElement | null;
    const ticker = document.querySelector('#price-ticker') as HTMLElement | null;
    const offset =
      (header?.getBoundingClientRect().height ?? 0) + (ticker?.getBoundingClientRect().height ?? 0);
    const top = window.scrollY + target.getBoundingClientRect().top - offset - 16;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  // AI 상담 전용 페이지(/chat, 다른 사이트 팝업 안)와 관리자 화면에서는 배경음악·시세 버튼을 띄우지 않는다
  // (관리자 폰 화면에서는 상단 바·메뉴 버튼을 가린다)
  if (pathname?.startsWith('/chat') || pathname?.startsWith('/admin')) return null;

  return (
    <>
      <button data-component-id="src/components/common/GlobalAudioPlayer.tsx:145:6" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="145" data-component-end-line="181" data-component-file="GlobalAudioPlayer.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22fixed%20right-%5Bcalc(env(safe-area-inset-right)%2B5rem)%5D%20sm%3Aright-%5Bcalc(env(safe-area-inset-right)%2B9.5rem)%5D%20z-%5B9000%5D%20inline-flex%20items-center%20gap-2%20rounded-full%20border%20border-border%20bg-white%2F90%20text-text-primary%20shadow-conversion%20backdrop-blur%20px-3%20py-2%20hover%3Abg-white%20active%3Abg-white%20transition-smooth%22%7D"
        type="button"
        onClick={scrollToPricing}
        className="fixed right-[calc(env(safe-area-inset-right)+5rem)] sm:right-[calc(env(safe-area-inset-right)+9.5rem)] z-[9000] inline-flex items-center gap-2 rounded-full border border-border bg-white/90 text-text-primary shadow-conversion backdrop-blur px-3 py-2 hover:bg-white active:bg-white transition-smooth"
        style={{ top: `calc(env(safe-area-inset-top) + ${topOffsetPx}px)` }}
        aria-label="시세 이동"
      >
        <span data-component-id="src/components/common/GlobalAudioPlayer.tsx:152:8" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="152" data-component-end-line="179" data-component-file="GlobalAudioPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22inline-flex%20items-center%20justify-center%20w-9%20h-9%20rounded-full%20border%20bg-secondary%2F60%20border-border%20text-text-secondary%22%7D" className="inline-flex items-center justify-center w-9 h-9 rounded-full border bg-secondary/60 border-border text-text-secondary">
          <svg data-component-id="src/components/common/GlobalAudioPlayer.tsx:153:10" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="153" data-component-end-line="178" data-component-file="GlobalAudioPlayer.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path data-component-id="src/components/common/GlobalAudioPlayer.tsx:154:12" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="154" data-component-end-line="160" data-component-file="GlobalAudioPlayer.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              d="M4 18V6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path data-component-id="src/components/common/GlobalAudioPlayer.tsx:161:12" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="161" data-component-end-line="167" data-component-file="GlobalAudioPlayer.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              d="M4 18H20"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path data-component-id="src/components/common/GlobalAudioPlayer.tsx:168:12" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="168" data-component-end-line="174" data-component-file="GlobalAudioPlayer.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              d="M6 14L10 10L14 13L19 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle data-component-id="src/components/common/GlobalAudioPlayer.tsx:175:12" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="175" data-component-end-line="175" data-component-file="GlobalAudioPlayer.tsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%7D" cx="10" cy="10" r="1.6" fill="currentColor" />
            <circle data-component-id="src/components/common/GlobalAudioPlayer.tsx:176:12" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="176" data-component-end-line="176" data-component-file="GlobalAudioPlayer.tsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%7D" cx="14" cy="13" r="1.6" fill="currentColor" />
            <circle data-component-id="src/components/common/GlobalAudioPlayer.tsx:177:12" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="177" data-component-end-line="177" data-component-file="GlobalAudioPlayer.tsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%7D" cx="19" cy="7" r="1.6" fill="currentColor" />
          </svg>
        </span>
        <span data-component-id="src/components/common/GlobalAudioPlayer.tsx:180:8" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="180" data-component-end-line="180" data-component-file="GlobalAudioPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%22%2C%22textContent%22%3A%22%EC%8B%9C%EC%84%B8%22%7D" className="text-sm font-medium">시세</span>
      </button>
      <button data-component-id="src/components/common/GlobalAudioPlayer.tsx:182:6" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="182" data-component-end-line="252" data-component-file="GlobalAudioPlayer.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22fixed%20right-%5Bcalc(env(safe-area-inset-right)%2B0.75rem)%5D%20z-%5B9000%5D%20inline-flex%20items-center%20gap-2%20rounded-full%20border%20border-border%20bg-white%2F90%20text-text-primary%20shadow-conversion%20backdrop-blur%20px-3%20py-2%20hover%3Abg-white%20active%3Abg-white%20transition-smooth%22%7D"
        type="button"
        onClick={toggle}
        className="fixed right-[calc(env(safe-area-inset-right)+0.75rem)] z-[9000] inline-flex items-center gap-2 rounded-full border border-border bg-white/90 text-text-primary shadow-conversion backdrop-blur px-3 py-2 hover:bg-white active:bg-white transition-smooth"
        style={{ top: `calc(env(safe-area-inset-top) + ${topOffsetPx}px)` }}
        aria-label={enabled ? '음악 끄기' : '음악 켜기'}
      >
        <span data-component-id="src/components/common/GlobalAudioPlayer.tsx:189:8" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="189" data-component-end-line="250" data-component-file="GlobalAudioPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
          className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition-smooth ${
            enabled ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-secondary/60 border-border text-text-secondary'
          }`}
          aria-hidden
        >
          {enabled ? (
            <svg data-component-id="src/components/common/GlobalAudioPlayer.tsx:196:12" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="196" data-component-end-line="218" data-component-file="GlobalAudioPlayer.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path data-component-id="src/components/common/GlobalAudioPlayer.tsx:197:14" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="197" data-component-end-line="203" data-component-file="GlobalAudioPlayer.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                d="M9 18V6l12-2v12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path data-component-id="src/components/common/GlobalAudioPlayer.tsx:204:14" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="204" data-component-end-line="210" data-component-file="GlobalAudioPlayer.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                d="M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path data-component-id="src/components/common/GlobalAudioPlayer.tsx:211:14" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="211" data-component-end-line="217" data-component-file="GlobalAudioPlayer.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                d="M21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg data-component-id="src/components/common/GlobalAudioPlayer.tsx:220:12" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="220" data-component-end-line="248" data-component-file="GlobalAudioPlayer.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path data-component-id="src/components/common/GlobalAudioPlayer.tsx:221:14" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="221" data-component-end-line="227" data-component-file="GlobalAudioPlayer.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                d="M9 18V6l12-2v12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path data-component-id="src/components/common/GlobalAudioPlayer.tsx:228:14" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="228" data-component-end-line="234" data-component-file="GlobalAudioPlayer.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                d="M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path data-component-id="src/components/common/GlobalAudioPlayer.tsx:235:14" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="235" data-component-end-line="241" data-component-file="GlobalAudioPlayer.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                d="M21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path data-component-id="src/components/common/GlobalAudioPlayer.tsx:242:14" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="242" data-component-end-line="247" data-component-file="GlobalAudioPlayer.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                d="M4 4l16 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          )}
        </span>
        <span data-component-id="src/components/common/GlobalAudioPlayer.tsx:251:8" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="251" data-component-end-line="251" data-component-file="GlobalAudioPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22hidden%20sm%3Ainline%20text-sm%20font-medium%22%7D" className="hidden sm:inline text-sm font-medium">{enabled ? 'BGM ON' : 'BGM OFF'}</span>
      </button>
      <audio data-component-id="src/components/common/GlobalAudioPlayer.tsx:253:6" data-component-path="src/components/common/GlobalAudioPlayer.tsx" data-component-line="253" data-component-end-line="259" data-component-file="GlobalAudioPlayer.tsx" data-component-name="audio" data-component-content="%7B%22elementName%22%3A%22audio%22%7D"
        ref={audioRef}
        preload="auto"
        playsInline
        onEnded={handleEnded}
        style={{ display: 'none' }}
      />
    </>
  );
}
